<template>
  <section id="features" class="feature-section relative py-8 md:py-16 pb-16 md:pb-20 overflow-hidden bg-gradient-to-b from-[#1E2652] to-[#141B3D]">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <!-- Section Header -->
      <div class="text-center mb-16 md:mb-24">
        <h2 ref="titleRef" class="text-4xl md:text-6xl font-bold text-white mb-4 leading-tight">
          Rejoignez une communauté <span class="text-[#B7E000]">active</span>
        </h2>
      </div>

      <!-- iPhone Mockups Section -->
      <div class="iphone-mockups-section mb-32 relative">
        <!-- Background effect -->
        <div class="absolute inset-0 -top-20 -bottom-20 flex justify-center items-center pointer-events-none">
          <div class="w-96 h-96 bg-[#B7E000]/5 rounded-full filter blur-3xl"></div>
        </div>
        
        <div class="flex justify-center relative z-10">
          <div class="relative w-64 h-80 sm:w-72 sm:h-84 md:w-80 md:h-96 lg:w-96 lg:h-[28rem]">
            <!-- First iPhone (page_1.png) - Always visible -->
            <div ref="firstMockup" class="absolute inset-0 z-30 scale-125">
              <img 
                src="../assets/iphone-mockups/page_1.png" 
                alt="PadelGo App - Home Screen" 
                class="w-full h-full object-contain drop-shadow-2xl"
              />
            </div>
            
            <!-- Second iPhone (page_2.png) - Appears on scroll from the left -->
            <div ref="secondMockup" class="absolute inset-0 z-20 opacity-0 transform translate-x-8 sm:translate-x-12 md:translate-x-16 scale-95 -rotate-1">
              <img 
                src="../assets/iphone-mockups/Page_2.png" 
                alt="PadelGo App - Matches Screen" 
                class="w-full h-full object-contain drop-shadow-2xl"
              />
            </div>
            
            <!-- Third iPhone (page_3.png) - Appears on scroll from the right -->
            <div ref="thirdMockup" class="absolute inset-0 z-10 opacity-0 transform -translate-x-8 sm:-translate-x-12 md:-translate-x-16 scale-95 rotate-1">
              <img 
                src="../assets/iphone-mockups/page_3.png" 
                alt="PadelGo App - Profile Screen" 
                class="w-full h-full object-contain drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
        
      </div>

      <!-- Feature Grid: 2 Columns -->
      <div class="feature-grid grid grid-cols-1 md:grid-cols-2 gap-8">
        <div 
          v-for="(feature, index) in features" 
          :key="index"
          class="feature-card group"
        >
          <div class="bg-white/5 backdrop-blur-sm rounded-3xl p-8 h-full transition-all duration-300 hover:bg-white/10 border border-white/10 hover:border-[#B7E000]/30 overflow-hidden relative">
            <component 
              :is="feature.icon" 
              class="w-12 h-12 text-[#B7E000] mb-6"
            />

            <h3 class="text-2xl font-semibold text-white mb-4">{{ feature.title }}</h3>
            <p class="text-gray-400 mb-6">{{ feature.description }}</p>
            
            <ul class="space-y-2">
              <li v-for="(benefit, idx) in feature.benefits" :key="idx" class="flex items-start">
                <CheckCircle class="w-5 h-5 text-[#B7E000] mt-1 mr-2 flex-shrink-0" />
                <span class="text-gray-300 text-sm">{{ benefit }}</span>
              </li>
            </ul>

            <div class="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-[#B7E000] to-[#1E2652] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
          </div>
        </div>
      </div>

      <!-- Subtitle for iPhone mockups -->
      <div class="text-center mt-8 md:mt-12">
        <p ref="subtitleRef" class="text-xl text-gray-400 max-w-2xl mx-auto">
          Chez PadelGo, notre objectif est de transformer votre expérience de jeu en une expérience unique et enrichissante.
        </p>
      </div>
    </div>
    
    <!-- Decorative Elements -->
    <div class="absolute inset-0 pointer-events-none">
      <div class="absolute top-1/4 left-0 w-96 h-96 bg-[#B7E000]/10 rounded-full filter blur-3xl"></div>
      <div class="absolute bottom-1/4 right-0 w-96 h-96 bg-[#B7E000]/10 rounded-full filter blur-3xl"></div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { CalendarDays, Trophy, Lightbulb, Users, CheckCircle } from 'lucide-vue-next'

gsap.registerPlugin(ScrollTrigger)

const titleRef = ref<HTMLElement | null>(null)
const subtitleRef = ref<HTMLElement | null>(null)
const firstMockup = ref<HTMLElement | null>(null)
const secondMockup = ref<HTMLElement | null>(null)
const thirdMockup = ref<HTMLElement | null>(null)

const features = [
  {
    icon: CalendarDays,
    title: 'Réservation',
    description: 'Réservez des terrains auprès de nos clubs partenaires.',
    benefits: ['Disponibilité des terrains en temps réel', "Réservation simple et rapide", "Annulation flexible"]
  },
  {
    icon: Users,
    title: 'Matchmaking',
    description: 'Trouvez des partenaires de jeu ou des adversaires de même niveau autour de vous.',
    benefits: ['Créez ou rejoignez des matchs selon vos critères', 'Matchs amicaux et compétitifs', 'Système de niveaux intégré']
  },
  {
    icon: Lightbulb,
    title: 'Coaching',
    description: 'Réservez des cours avec nos coachs partenaires.',
    benefits: ['Perfectionnez votre niveau et votre style de jeu', 'Cours collectifs et particuliers']
  },
  {
    icon: Trophy,
    title: 'Tournois',
    description: 'Participez à des tournois et des événements organisés par nos clubs partenaires.',
    benefits: ['Systèmes de classements et de communauté', 'Récompenses exclusives pour les vainqueurs']
  }
]

onMounted(() => {
  gsap.from(titleRef.value, {
    y: 50,
    opacity: 0,
    duration: 1,
    ease: 'power3.out',
    scrollTrigger: { trigger: titleRef.value, start: 'top 80%' }
  })

  gsap.from(subtitleRef.value, {
    y: 30,
    opacity: 0,
    duration: 1,
    delay: 0.2,
    ease: 'power3.out',
    scrollTrigger: { trigger: subtitleRef.value, start: 'top 80%' }
  })

  gsap.utils.toArray('.feature-card').forEach((card: any, index: number) => {
    gsap.from(card, {
      y: 50,
      opacity: 0,
      duration: 0.8,
      delay: 0.1 * index,
      ease: 'power3.out',
      scrollTrigger: { trigger: card, start: 'top 85%' }
    })
  })

  // iPhone Mockups scroll animations
  if (secondMockup.value && thirdMockup.value) {
    // First mockup entrance animation
    gsap.from(firstMockup.value, {
      y: 30,
      opacity: 0,
      duration: 1,
      ease: 'power3.out',
      scrollTrigger: { 
        trigger: firstMockup.value, 
        start: 'top 85%' 
      }
    })

    // Second mockup appears on scroll from the left
    gsap.to(secondMockup.value, {
      opacity: 1,
      x: '-50%',
      scale: 1,
      rotation: -5,
      duration: 1.5,
      ease: 'power3.out',
      scrollTrigger: { 
        trigger: secondMockup.value, 
        start: 'top 70%',
        toggleActions: 'play none none reverse'
      }
    })

    // Third mockup appears on scroll from the right
    gsap.to(thirdMockup.value, {
      opacity: 1,
      x: '50%',
      scale: 1,
      rotation: 5,
      duration: 1.5,
      delay: 0.2,
      ease: 'power3.out',
      scrollTrigger: { 
        trigger: thirdMockup.value, 
        start: 'top 70%',
        toggleActions: 'play none none reverse'
      }
    })
  }
})
</script>

<style scoped>
.feature-grid {
  /* Ensures each row is equal in height */
  grid-auto-rows: 1fr;
}

.feature-card {
  transition: all 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-5px);
}

.iphone-mockups-section {
  perspective: 1000px;
}

.iphone-mockups-section img {
  transition: all 0.3s ease;
}

.iphone-mockups-section img:hover {
  transform: scale(1.05);
}

/* Ensure smooth scrolling to the features section */
#features {
  scroll-margin-top: 100px; /* Account for fixed header */
  scroll-behavior: smooth;
}
</style>
